import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import type { AxiosResponse } from 'axios';

import type {
  DeleteAccountSuccess,
  DeleteAccountFailure,
} from '../modules/graphql/types';

import { createClient } from './graphql';

interface ApiOptions {
  mockAuthOptions?: {
    authenticated: boolean;
  };
  useMocks: boolean;
  isServer?: boolean;
  zone: Zone;
}

export interface ApiInterface {
  getUser(): Promise<any>;
  deleteAccount(): Promise<DeleteAccountSuccess | DeleteAccountFailure>;
  changeEmail({
    newEmail,
    currentPassword,
  }: {
    newEmail: string;
    currentPassword: string;
  }): Promise<AxiosResponse>;
  changeEmailStart({ newEmail }: { newEmail: string }): Promise<AxiosResponse>;
  changeEmailVerify({
    newEmail,
    verificationCode,
  }: {
    newEmail: string;
    verificationCode?: string;
  }): Promise<AxiosResponse>;
  graphqlClient: ApolloClient<NormalizedCacheObject>;
}

export default ({ useMocks, mockAuthOptions, isServer, zone }: ApiOptions) => {
  const appName = 'SEEK Settings';

  const authOptions = {
    appName,
    useMockedAuth: useMocks,
    mockOptions: mockAuthOptions,
    useAuth: true,
    forceStrategy: 'AUTH0',
  };

  const graphqlClient = createClient(
    authOptions,
    appName,
    useMocks,
    zone,
    isServer,
  );
  const api: ApiInterface = {
    getUser: async () => graphqlClient.getUser(),
    changeEmail: async ({ newEmail, currentPassword }) =>
      graphqlClient.changeEmail({
        newEmailAddress: newEmail,
        password: currentPassword,
      }),
    deleteAccount: () => graphqlClient.deleteAccount(),
    changeEmailStart: async ({ newEmail }) =>
      graphqlClient.changeEmailStart({
        newEmailAddress: newEmail,
      }),
    changeEmailVerify: async ({ newEmail, verificationCode }) =>
      graphqlClient.changeEmailVerify({
        newEmailAddress: newEmail,
        verificationCode,
      }),
    graphqlClient: graphqlClient.client,
  };

  return api;
};
